<template>
    <div class="display-creative">
        <!-- Text Creative -->
        <div v-if="isText" class="creative-text" v-html="creative.text"></div>

        <!-- Media Creative -->
        <div v-else-if="hasMedia" class="display-media">
            <!-- Image -->
            <img v-if="previewType === 'image'" :src="mediaUrl" alt="Creative" class="img-fluid" />

            <!-- Audio -->
            <div v-else-if="previewType === 'audio'" class="center-media-container">
                <audio controls>
                    <source :src="mediaUrl" />
                    Your browser does not support the audio tag.
                </audio>
            </div>

            <!-- Video -->
            <div v-else-if="previewType === 'video'" class="center-media-container">
                <video width="100%" height="auto" style="max-width: 100%; max-height: 100%; min-width: 100px; min-height: 100px" controls>
                    <source :src="mediaUrl" :type="mimeType" />
                    Your browser does not support the video tag.
                </video>
            </div>

            <!-- PDF -->
            <div v-else-if="previewType === 'pdf'" class="embed-responsive embed-responsive-1by1">
                <embed :src="mediaUrl" type="application/pdf" class="embed-responsive-item" />
            </div>

            <!-- Office Documents (via Google Docs viewer) -->
            <div v-else-if="previewType === 'office'" class="embed-responsive embed-responsive-1by1">
                <iframe :src="'https://docs.google.com/viewer?url=' + encodeURIComponent(mediaUrl) + '&embedded=true'" class="embed-responsive-item"></iframe>
            </div>

            <!-- Fallback: download link -->
            <div v-else class="text-center py-3">
                <a :href="mediaUrl" target="_blank" class="btn btn-outline-primary">
                    <i class="fa fa-download mr-2"></i>
                    Download File
                </a>
            </div>
        </div>

        <!-- No content -->
        <div v-else class="text-muted text-center py-3">No creative content available</div>

        <!-- Description -->
        <div v-if="creative && creative.description" class="creative-description m-2">
            {{ creative.description }}
        </div>
    </div>
</template>

<script>
export default {
    name: 'CreativeDisplay',

    props: {
        /**
         * Creative object with properties:
         * - creative_type: 'text' | 'image' | 'audio_video' | etc.
         * - text: string (for text creative)
         * - media_url: string (URL to media file)
         * - media: object (media details with mime_type, url, etc.)
         * - description: string (optional description)
         */
        creative: {
            type: Object,
            required: true,
        },
    },

    computed: {
        /**
         * Check if this is a text creative.
         */
        isText() {
            return this.creative.creative_type === 'text';
        },

        /**
         * Check if media is available.
         */
        hasMedia() {
            return this.mediaUrl !== null;
        },

        /**
         * Get the media URL.
         */
        mediaUrl() {
            // Try various possible properties
            if (this.creative.media_url) {
                return this.creative.media_url;
            }
            if (this.creative.media && this.creative.media.url) {
                return this.creative.media.url;
            }
            return null;
        },

        /**
         * Determine the preview type based on mime type or creative type.
         */
        previewType() {
            const mimeType = this.mimeType;
            console.log(mimeType);

            if (!mimeType) {
                // Fallback to creative_type
                if (this.creative.creative_type === 'image') return 'image';
                if (this.creative.creative_type === 'audio_video') return 'video';
                return null;
            }

            if (mimeType.startsWith('image/')) return 'image';
            // Treat MP4 as video regardless of audio/video MIME type
            // (audio/mp4 can still be played by video element)
            if (mimeType === 'audio/mp4' || mimeType === 'video/mp4') return 'video';
            if (mimeType.startsWith('video/')) return 'video';
            if (mimeType.startsWith('audio/')) return 'audio';
            if (mimeType === 'application/pdf') return 'pdf';
            if (this.isOfficeMimeType(mimeType)) return 'office';

            return null;
        },

        /**
         * Get the mime type from media object.
         */
        mimeType() {
            if (this.creative.media && this.creative.media.mime_type) {
                return this.creative.media.mime_type;
            }
            return null;
        },
    },

    methods: {
        /**
         * Check if mime type is an Office document.
         */
        isOfficeMimeType(mimeType) {
            const officeTypes = [
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-powerpoint',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            ];
            return officeTypes.includes(mimeType);
        },
    },
};
</script>

<style scoped>
.display-creative {
    width: 100%;
}

.creative-text {
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 4px;
    text-align: left;
    max-height: 300px;
    overflow-y: auto;
}

.display-media {
    display: flex;
    justify-content: center;
    align-items: center;
}

.display-media img {
    max-width: 100%;
    max-height: 400px;
    object-fit: contain;
}

.center-media-container {
    width: 100%;
    display: flex;
    justify-content: center;
}

.center-media-container audio {
    width: 100%;
    max-width: 400px;
}

.embed-responsive {
    position: relative;
    width: 100%;
    padding-bottom: 100%; /* 1:1 aspect ratio */
}

.embed-responsive-item {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
}

.creative-description {
    font-size: 0.9em;
    color: #666;
    text-align: center;
    margin-top: 10px;
}
</style>
